import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveTab, closeTab, closeAllTabs, closeOtherTabs } from '../../store/slices/TabsSlice';
import styled from 'styled-components';
import { IconButton, Menu, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const TabBarContainer = styled.div`
  display: flex;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 2px solid #e0e0e0;
  overflow-x: auto;
  overflow-y: hidden;
  height: 40px;
  margin-left: var(--sidebar-width, 220px);
  position: fixed;
  top: 55px;
  right: 0;
  left: 0;
  z-index: 100;
  transition: margin-left 0.2s ease-in-out;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
`;

const TabsWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`;

const Tab = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: ${props => props.active ? '#fff' : 'transparent'};
  border-right: 1px solid #e0e0e0;
  cursor: pointer;
  min-width: 120px;
  max-width: 200px;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#4281AB' : '#666'};
  white-space: nowrap;
  transition: background 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#fff' : '#e8e8e8'};
  }
  
  ${props => props.active && `
    border-bottom: 2px solid #4281AB;
    margin-bottom: -2px;
  `}
`;

const TabTitle = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 4px;
`;

const TabCloseButton = styled(IconButton)`
  && {
    padding: 2px;
    margin-left: 4px;
    opacity: ${props => props.active ? 1 : 0.6};
    
    &:hover {
      opacity: 1;
      background: rgba(0, 0, 0, 0.1);
    }
    
    svg {
      font-size: 16px;
    }
  }
`;

const TabMenuButton = styled(IconButton)`
  && {
    padding: 4px 8px;
    margin: 0 4px;
    
    svg {
      font-size: 20px;
    }
  }
`;

export const TabBar = () => {
  const dispatch = useDispatch();
  const { tabs, activeTabId } = useSelector(state => state.tabs);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [contextTab, setContextTab] = React.useState(null);

  const handleTabClick = (tabId) => {
    dispatch(setActiveTab(tabId));
  };

  const handleCloseTab = (e, tabId) => {
    e.stopPropagation();
    dispatch(closeTab(tabId));
  };

  const handleMenuOpen = (e) => {
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setContextTab(null);
  };

  const handleCloseAll = () => {
    dispatch(closeAllTabs());
    handleMenuClose();
  };

  const handleCloseOthers = () => {
    if (contextTab || activeTabId) {
      dispatch(closeOtherTabs(contextTab || activeTabId));
    }
    handleMenuClose();
  };

  const handleTabContextMenu = (e, tabId) => {
    e.preventDefault();
    setContextTab(tabId);
    setMenuAnchor({ left: e.clientX, top: e.clientY });
  };

  return (
    <TabBarContainer>
      <TabsWrapper>
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            active={tab.id === activeTabId}
            onClick={() => handleTabClick(tab.id)}
            onContextMenu={(e) => handleTabContextMenu(e, tab.id)}
          >
            <TabTitle title={tab.title}>{tab.title}</TabTitle>
            {tab.closable && (
              <TabCloseButton
                size="small"
                onClick={(e) => handleCloseTab(e, tab.id)}
                active={tab.id === activeTabId}
              >
                <CloseIcon />
              </TabCloseButton>
            )}
          </Tab>
        ))}
      </TabsWrapper>
      
      <TabMenuButton size="small" onClick={handleMenuOpen}>
        <MoreHorizIcon />
      </TabMenuButton>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorReference={menuAnchor?.left ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={menuAnchor?.left ? { top: menuAnchor.top, left: menuAnchor.left } : undefined}
      >
        <MenuItem onClick={handleCloseOthers}>Close Others</MenuItem>
        <MenuItem onClick={handleCloseAll}>Close All</MenuItem>
      </Menu>
    </TabBarContainer>
  );
};
